import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * <cod-order-form>
 * Custom element that powers the COD (cash-on-delivery) offer box:
 * - Package (variant) selection via radio cards
 * - Updates price / compare-at / savings display
 * - Validates and submits the COD form (nume, telefon, adresă, cod poștal, oraș)
 * - Adds the selected variant to cart with the form data as line item
 *   properties, then redirects straight to /checkout (COD-only store,
 *   no card payment method enabled).
 */
class CodOrderForm extends HTMLElement {
  connectedCallback() {
    // A "secondary" instance (e.g. the final-CTA package repeater) has its
    // own radio group (different `name`, so it doesn't fight the primary's
    // native radio exclusivity) and no dialog/form of its own — it only
    // selects a package, then mirrors that choice onto the primary instance
    // and delegates to its "Comandă acum" button / dialog / checkout flow.
    this.isSecondary = this.hasAttribute('data-secondary');

    this.form = this.querySelector('[ref="codForm"]');
    this.radios = Array.from(this.querySelectorAll('[data-package-radio]'));
    this.submitButton = this.querySelector('[ref="codSubmitButton"]');
    this.errorBox = this.querySelector('[ref="codFormError"]');
    this.dialog = this.querySelector('[ref="codDialog"]');
    this.dialogCloseButton = this.querySelector('[ref="codDialogClose"]');
    this.orderButton = this.querySelector('[ref="codOrderButton"]');

    this.radios.forEach((radio) => {
      radio.addEventListener('change', () => {
        if (this.isSecondary) {
          this.updateOwnCardStyling();
          this.syncToPrimary(radio);
        } else {
          this.handlePackageChange();
        }
      });
    });

    // Cards only select a package now — the dialog opens exclusively from
    // the "Comandă acum" button below, so it always reflects whichever
    // package is selected at the moment of the click.
    if (this.orderButton) {
      this.orderButton.addEventListener('click', () => {
        if (this.isSecondary) {
          // No dialog/form of its own — make sure the primary instance has
          // the right variant selected, then reuse its own button click
          // (opens the one real order dialog, already showing the price
          // that matches what was just picked here).
          const selected = this.getSelectedRadio();
          if (selected) this.syncToPrimary(selected);
          const primary = CodOrderForm.getPrimary();
          if (primary && primary.orderButton) {
            primary.orderButton.click();
          }
          return;
        }

        this.handlePackageChange();
        if (this.dialog && typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
          lockScroll(this.dialog);
        }
      });
    }

    if (this.dialogCloseButton) {
      this.dialogCloseButton.addEventListener('click', () => this.dialog.close());
    }

    if (this.dialog) {
      // The dialog itself has no padding of its own (header/body each carry
      // their own), so a click that lands directly on it — not on a
      // descendant — can only be a click on the backdrop. Closes on
      // backdrop click, never on a click inside the header/body content.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

      // The native 'close' event fires for every way a <dialog> can close —
      // the button above, backdrop click, or the Escape key — so this is
      // the one place that reliably releases the scroll lock regardless of
      // which path closed it.
      this.dialog.addEventListener('close', () => unlockScroll(this.dialog));
    }

    if (this.form) {
      this.form.addEventListener('submit', (event) => this.handleSubmit(event));
    }

    if (this.isSecondary) {
      this.updateOwnCardStyling();
    } else {
      this.handlePackageChange();
    }
  }

  static getPrimary() {
    return document.querySelector('cod-order-form:not([data-secondary])');
  }

  getSelectedRadio() {
    return this.radios.find((radio) => radio.checked) || this.radios[0];
  }

  // Toggles this instance's own `.is-selected` card styling to match
  // whichever of its local radios is checked — used by secondary instances,
  // since handlePackageChange() only updates the primary's own cards.
  // Compared by `.value` (variant id), not node identity, because the
  // primary instance itself can render the same package list twice (the
  // page and the order dialog both live inside it) — two different <input>
  // nodes can share the same variant, and both should light up together.
  updateOwnCardStyling() {
    const selected = this.getSelectedRadio();
    this.querySelectorAll('[data-package-card]').forEach((card) => {
      const input = card.querySelector('[data-package-radio]');
      card.classList.toggle('is-selected', input.value === selected.value);
    });
  }

  // Mirrors a secondary instance's selected variant onto the primary
  // instance's matching radio (same variant id as `value`), so the actual
  // order form/checkout — which only ever reads the primary's own radios —
  // submits whatever was picked here.
  syncToPrimary(radio) {
    const primary = CodOrderForm.getPrimary();
    if (!primary) return;

    const match = primary.radios.find((candidate) => candidate.value === radio.value);
    if (!match) return;

    if (!match.checked) {
      match.checked = true;
      match.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      primary.handlePackageChange();
    }
  }

  handlePackageChange() {
    const selected = this.getSelectedRadio();
    if (!selected) return;

    const price = selected.dataset.price;
    const comparePrice = selected.dataset.comparePrice;
    const savings = selected.dataset.savings;
    const savingsAmount = selected.dataset.savingsAmount;
    const available = selected.dataset.available;

    // Price displays live in three places (price-hero, form summary, sticky
    // bar) — the sticky bar sits outside this custom element, so these are
    // queried document-wide rather than scoped to `this`.
    document.querySelectorAll('[data-cod-current-price]').forEach((el) => {
      el.textContent = price;
    });
    document.querySelectorAll('[data-cod-compare-price]').forEach((el) => {
      if (comparePrice) {
        el.textContent = comparePrice;
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });
    document.querySelectorAll('[data-cod-savings]').forEach((el) => {
      if (savings) {
        el.textContent = savings;
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });

    document.querySelectorAll('[data-cod-savings-amount]').forEach((el) => {
      el.textContent = savingsAmount || '';
    });
    document.querySelectorAll('[data-cod-savings-amount-label]').forEach((el) => {
      el.hidden = !savingsAmount;
    });
    document.querySelectorAll('[data-cod-stock-status]').forEach((el) => {
      el.textContent = available === 'true' ? '🟢 În stoc' : 'Stoc epuizat';
    });

    this.querySelectorAll('[data-package-card]').forEach((card) => {
      const input = card.querySelector('[data-package-radio]');
      card.classList.toggle('is-selected', input.value === selected.value);
    });

    // Keep any secondary instance's own cards (e.g. the final-CTA package
    // repeater) visually in sync when the selection changes from here —
    // direct assignment only, no dispatched event, so this can't loop back
    // into syncToPrimary().
    if (!this.isSecondary) {
      document.querySelectorAll('cod-order-form[data-secondary]').forEach((secondary) => {
        const match = secondary.radios?.find((candidate) => candidate.value === selected.value);
        if (match) {
          match.checked = true;
          secondary.updateOwnCardStyling?.();
        }
      });
    }
  }

  clearErrors() {
    this.querySelectorAll('.cod-field__error').forEach((el) => {
      el.textContent = '';
    });
    this.querySelectorAll('.cod-field--invalid').forEach((el) => {
      el.classList.remove('cod-field--invalid');
    });
    if (this.errorBox) {
      this.errorBox.hidden = true;
      this.errorBox.textContent = '';
    }
  }

  validate(data) {
    const errors = {};

    if (!data.nume || data.nume.trim().length < 3) {
      errors.nume = 'Te rugăm introdu numele și prenumele.';
    }

    const phoneDigits = (data.telefon || '').replace(/[^0-9+]/g, '');
    if (!/^(\+?4?0)?7\d{8}$/.test(phoneDigits) && !/^0\d{9}$/.test(phoneDigits)) {
      errors.telefon = 'Introdu un număr de telefon valid (ex: 07XXXXXXXX).';
    }

    if (!data.judet) {
      errors.judet = 'Te rugăm alege județul.';
    }

    if (!data.oras || data.oras.trim().length < 2) {
      errors.oras = 'Te rugăm introdu orașul.';
    }

    if (!data.adresa || data.adresa.trim().length < 5) {
      errors.adresa = 'Te rugăm introdu adresa (stradă, număr).';
    }

    // Cod poștal is optional — only validated if the customer typed something.
    if (data.cod_postal && data.cod_postal.trim().length < 3) {
      errors.cod_postal = 'Codul poștal introdus nu pare complet.';
    }

    return errors;
  }

  showErrors(errors) {
    Object.entries(errors).forEach(([name, message]) => {
      const field = this.form.querySelector(`[data-field="${name}"]`);
      const errorEl = field?.querySelector('.cod-field__error');
      if (field) field.classList.add('cod-field--invalid');
      if (errorEl) errorEl.textContent = message;
    });
  }

  async handleSubmit(event) {
    event.preventDefault();
    this.clearErrors();

    const selected = this.getSelectedRadio();
    if (!selected) return;

    const formData = new FormData(this.form);
    const data = Object.fromEntries(formData.entries());
    const errors = this.validate(data);

    if (Object.keys(errors).length > 0) {
      this.showErrors(errors);
      const firstInvalid = this.form.querySelector('.cod-field--invalid input, .cod-field--invalid textarea');
      firstInvalid?.focus();
      return;
    }

    this.submitButton.disabled = true;
    this.submitButton.classList.add('is-loading');

    try {
      await fetch('/cart/clear.js', { method: 'POST' });

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(selected.value),
              quantity: 1,
              properties: {
                Nume: data.nume,
                Telefon: data.telefon,
                Județ: data.judet,
                Oraș: data.oras,
                Adresă: data.adresa,
                'Cod poștal': data.cod_postal || '',
                'Metodă de plată': 'Ramburs la livrare (COD)',
              },
            },
          ],
        }),
      });

      if (!addResponse.ok) {
        throw new Error('cart-add-failed');
      }

      const [firstName, ...lastNameParts] = data.nume.trim().split(/\s+/);
      const checkoutParams = new URLSearchParams({
        'checkout[shipping_address][first_name]': firstName || '',
        'checkout[shipping_address][last_name]': lastNameParts.join(' ') || '',
        'checkout[shipping_address][province]': data.judet,
        'checkout[shipping_address][city]': data.oras,
        'checkout[shipping_address][address1]': data.adresa,
        'checkout[shipping_address][zip]': data.cod_postal || '',
        'checkout[shipping_address][phone]': data.telefon,
        'checkout[shipping_address][country]': 'RO',
      });

      window.location.href = `/checkout?${checkoutParams.toString()}`;
    } catch (error) {
      this.submitButton.disabled = false;
      this.submitButton.classList.remove('is-loading');
      if (this.errorBox) {
        this.errorBox.hidden = false;
        this.errorBox.textContent = 'A apărut o eroare la trimiterea comenzii. Te rugăm încearcă din nou.';
      }
    }
  }
}

if (!customElements.get('cod-order-form')) {
  customElements.define('cod-order-form', CodOrderForm);
}
